import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Loader2, PackageOpen, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import { API, formatAUD, downloadInvoice } from "../lib/shop";

export default function MyOrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (user)
      axios.get(`${API}/my-orders`).then((r) => setOrders(r.data)).catch(() => {
        setLoadError(true);
        setOrders([]);
      });
  }, [user]);

  if (user === undefined) return <div className="py-24 text-center"><Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" /></div>;
  if (user === null) return <Navigate to="/login" replace />;

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 md:px-8" data-testid="my-orders-page">
      <h1 className="text-3xl font-bold tracking-tight">My Orders</h1>
      {orders === null ? (
        <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>
      ) : loadError ? (
        <div className="py-16 text-center text-sm text-destructive" data-testid="my-orders-error">
          Could not load your orders. Please refresh the page.
        </div>
      ) : orders.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground" data-testid="my-orders-empty">
          <PackageOpen className="mx-auto mb-3 h-8 w-8 text-muted-foreground/40" />
          No orders yet. Orders placed while signed in will appear here.
        </div>
      ) : (
        <ul className="mt-8 space-y-4">
          {orders.map((o) => (
            <li key={o.id} className="rounded-sm border border-border bg-white p-5" data-testid={`my-order-${o.order_number}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <Link to={`/order/${o.id}`} className="font-mono text-sm font-bold text-primary hover:underline">
                    {o.order_number}
                  </Link>
                  <span className="ml-3 text-xs text-muted-foreground">{o.created_at.slice(0, 10)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={`rounded-sm ${o.status === "fulfilled" ? "bg-[#16a34a] hover:bg-[#16a34a]" : o.status === "pending_payment" ? "bg-muted text-muted-foreground hover:bg-muted" : "bg-accent text-accent-foreground hover:bg-accent"}`}>
                    {o.status === "pending_payment" ? "awaiting payment" : o.status}
                  </Badge>
                  <span className="font-semibold tabular-nums">{formatAUD(o.total)}</span>
                  {o.payment_status === "paid" && (
                    <button
                      onClick={() => downloadInvoice(axios, o).catch(() => toast.error("Could not download invoice"))}
                      data-testid={`invoice-download-${o.order_number}`}
                      title="Download tax invoice (PDF)"
                      className="flex items-center gap-1 rounded-sm border border-border px-2 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                    >
                      <FileDown className="h-3.5 w-3.5" /> Invoice
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                {o.items.length} line{o.items.length > 1 ? "s" : ""} · {o.items.map((i) => i.product_code).slice(0, 4).join(", ")}
                {o.items.length > 4 ? "…" : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
